import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USER_CURRENT_KEY } from '../../../api/public-voissa/users';
import { applyThemeForUser } from '../../../libs/theme';

/**
 * ThemeSync — composant invisible (return null) à monter au top-level.
 *
 * Synchronise le thème dès que l'utilisateur est connu, sans dépendre du
 * Menu/ThemeSwitch. Un admin avec localStorage='dark' verra son thème
 * appliqué immédiatement à l'arrivée sur le site.
 *
 * Implémentation : s'abonne directement au QueryCache React Query.
 * Dès que la query [USER_CURRENT_KEY] résout (déclenchée par useQueryCurrentUser
 * ailleurs avec enabled:true), on lit l'accès au thème et on l'applique.
 *
 * canUseTheme(user) (côté libs/theme.js) autorise Plus / Max / Admin (capability
 * canUseDarkMode) ; les autres sont forcés en light.
 */
const readThemeAccess = (queryClient) => {
    const user = queryClient.getQueryData([USER_CURRENT_KEY]);
    return {
        role: user?.role,
        canUseDarkMode: Boolean(user?.capabilities?.canUseDarkMode),
    };
};

const ThemeSync = () => {
    const queryClient = useQueryClient();
    const [access, setAccess] = useState(() => readThemeAccess(queryClient));
    const { role, canUseDarkMode } = access;

    // Subscribe au cache pour détecter quand la query user résout / change.
    useEffect(() => {
        const cache = queryClient.getQueryCache();
        const unsubscribe = cache.subscribe((event) => {
            const queryKey = event?.query?.queryKey;
            if (!queryKey || queryKey[0] !== USER_CURRENT_KEY) return;
            const next = readThemeAccess(queryClient);
            setAccess((prev) => (
                prev.role === next.role && prev.canUseDarkMode === next.canUseDarkMode
                    ? prev
                    : next
            ));
        });
        return unsubscribe;
    }, [queryClient]);

    // Applique le thème dès que l'accès change (login, logout, abonnement, rôle).
    useEffect(() => {
        applyThemeForUser({ role, capabilities: { canUseDarkMode } });
    }, [role, canUseDarkMode]);

    return null;
};

export default ThemeSync;
